/**
 * Reads a peer's live voice-playback audio into a small, renderer-agnostic signal — plan for the
 * visor voice visualisation: an RMS level (the on/quiet/speaking split `VisorVisualizer.ts` uses)
 * and a fixed-size array of normalised frequency-band levels (the bars themselves). This is
 * deliberately the *only* place either number is computed; `VisorVisualizer.ts`'s own module doc
 * explains why nothing downstream may know how a bar is drawn from it.
 *
 * Modelled directly on `assistant/PlaybackAmplitude.ts`: one `AnalyserNode` tapped off an
 * `<audio>` element via `createMediaElementSource`, sampled on a plain `setInterval` rather than
 * `requestAnimationFrame` (CLAUDE.md, "requestAnimationFrame is throttled inside an active WebXR
 * session" — this has to keep sampling on the headset, not just on desktop where rAF alone would
 * have looked fine and done nothing once a session opened). The same gotcha that file's own doc
 * names applies here too: `createMediaElementSource` redirects the element's *entire* output into
 * the graph the instant it is called, so tapping a peer's voice for analysis has to also
 * reconnect it to `audioContext.destination`, or that peer goes silent the moment this starts
 * listening (CLAUDE.md, "A WebAudio graph tapped off an <audio> element must still reach
 * destination").
 *
 * One `VoiceBands` instance per peer, matching `PeerVoicePlayer`'s own one-`MediaSource`-per-peer
 * shape — two people talking at once must read as two independent signals, not one blended one,
 * and an `AnalyserNode` has no way to keep two sources apart if they shared one.
 */

/** 12 bands, mirrored left/right on the visor for 24 bars total — see VisorVisualizer.ts. */
export const BAND_COUNT = 12;

/** Logarithmic band edges, matching how pitch and this project's own ear both work — a linear
 *  spacing would crowd every band into the bottom octave of a voice's own range. */
export const BAND_MIN_HZ = 80;
export const BAND_MAX_HZ = 6000;

/** 15 Hz, not `requestAnimationFrame` — see the module doc above for why. */
export const SAMPLE_INTERVAL_MS = 1000 / 15;

export interface VoiceBandsSample {
  /** `BAND_COUNT` values, normalised 0..1, band 0 = lowest frequency. This array is the
   *  interface `VisorVisualizer.ts` draws from — nothing here knows or cares how. */
  readonly bands: readonly number[];
  /** 0..1 RMS of the same tick's time-domain signal, and `voice/VoiceActivity.ts`'s
   *  `MIC_QUIET_RMS` decides "quiet" here — see VisorVisualizer.ts's own use of it. One shared
   *  threshold rather than an independently-tuned second one, so an avatar's own voice display
   *  cannot silently disagree with what "quiet" means anywhere else this project decides it. */
  readonly rms: number;
}

interface BinRange {
  readonly start: number;
  /** Exclusive. */
  readonly end: number;
}

/**
 * Which frequency bins (out of `binCount`, the `AnalyserNode`'s own `frequencyBinCount`) belong
 * to each of `BAND_COUNT` log-spaced bands between `BAND_MIN_HZ` and `BAND_MAX_HZ`, for an
 * analyser reading a context sampled at `sampleRate`.
 *
 * Bin `i`'s centre frequency is `i * sampleRate / fftSize`, and the Web Audio API guarantees
 * `frequencyBinCount === fftSize / 2`, so `fftSize` never needs its own parameter here — for any
 * real `AnalyserNode`, `binCount` alone already fixes it. `highest` clamps `BAND_MAX_HZ` down to
 * the context's own Nyquist frequency (`sampleRate / 2`) when that is lower than 6kHz — a
 * caller's `sampleRate` is a real, live `AudioContext.sampleRate`, never hardcoded, so this has
 * to hold even for a context sampled slower than 12kHz.
 *
 * Exported and pure so a test can pin exact bin indices for a known sample rate with no
 * `AudioContext` involved at all — jsdom has none, the same constraint
 * `assistant/PlaybackAmplitude.ts` already documents for the class that actually owns one.
 */
export function bandBinRanges(sampleRate: number, binCount: number): BinRange[] {
  const nyquist = sampleRate / 2;
  const highest = Math.min(BAND_MAX_HZ, nyquist);
  const binForHz = (hz: number) => clamp(Math.round((hz * binCount) / nyquist), 0, binCount);

  const edgeBins: number[] = [];
  for (let i = 0; i <= BAND_COUNT; i++) {
    const hz = BAND_MIN_HZ * (highest / BAND_MIN_HZ) ** (i / BAND_COUNT);
    edgeBins.push(binForHz(hz));
  }

  const ranges: BinRange[] = [];
  for (let band = 0; band < BAND_COUNT; band++) {
    const start = Math.min(edgeBins[band]!, binCount - 1);
    // Guarantees a non-empty range even where two successive log-spaced edges round to the same
    // bin (common at the low-frequency end, where bins are wide relative to the band spacing) —
    // every band gets at least one bin to read, never a silently-empty average.
    const end = Math.max(edgeBins[band + 1]!, start + 1);
    ranges.push({ start, end: Math.min(end, binCount) });
  }

  return ranges;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

/** Averages `frequencyBins` (0..255 each, `AnalyserNode.getByteFrequencyData`'s own range) within
 *  each of `ranges` into a 0..1 level. Pure and exported for the same reason `bandBinRanges` is. */
export function sampleBands(frequencyBins: Uint8Array, ranges: readonly BinRange[]): number[] {
  return ranges.map(({ start, end }) => {
    let sum = 0;
    let count = 0;

    for (let i = start; i < end; i++) {
      sum += frequencyBins[i] ?? 0;
      count++;
    }

    return count > 0 ? sum / count / 255 : 0;
  });
}

/**
 * Root-mean-square of raw 8-bit time-domain samples (128 = silence) — the identical five-line
 * formula `assistant/PlaybackAmplitude.ts` already uses, on its own signal (assistant-reply
 * playback). Not imported from it: that file reasons, in its own doc, about why sharing this
 * five-line formula across otherwise-unrelated concerns costs more than duplicating it, and a
 * peer's live voice signal is a second, equally unrelated concern.
 */
export function rmsOfTimeDomainBytes(samples: Uint8Array): number {
  let sumSquares = 0;

  for (let i = 0; i < samples.length; i++) {
    const centred = (samples[i]! - 128) / 128;
    sumSquares += centred * centred;
  }

  return Math.sqrt(sumSquares / samples.length);
}

/**
 * The real `AnalyserNode`/`AudioContext` plumbing — see the module doc for the shape it follows.
 * Every step in `start()` is wrapped in one try/catch, matching `PlaybackAmplitude.start()`: this
 * is best-effort instrumentation for a visual, and a failure here must never interrupt the voice
 * playback it is reading from, which does not depend on this class succeeding.
 */
export class VoiceBands {
  private audioContext: AudioContext | null = null;
  private intervalHandle: ReturnType<typeof setInterval> | null = null;

  /** Starts sampling `audio`'s own live output every `SAMPLE_INTERVAL_MS`, reported through
   *  `onSample`, until `stop()` is called. Stops any previous tracking first, so a caller never
   *  has to pair every `start()` with a `stop()` of its own before the next one — the same shape
   *  `PlaybackAmplitude.start()` already has. */
  start(audio: HTMLAudioElement, onSample: (sample: VoiceBandsSample) => void): void {
    this.stop();

    try {
      const audioContext = new AudioContext();
      const source = audioContext.createMediaElementSource(audio);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 256;
      // The AnalyserNode default — set explicitly so it is visible here rather than relied on
      // silently (the task's own instruction, and good practice regardless).
      analyser.smoothingTimeConstant = 0.8;

      source.connect(analyser);
      analyser.connect(audioContext.destination);

      // Best-effort, matching PlaybackAmplitude.start(): a freshly-constructed AudioContext may
      // start suspended until a user gesture unblocks it. Every path that can reach a peer's
      // voice at all already carries a genuine gesture earlier on the same page, so this
      // reliably resolves in practice; nothing here depends on it settling before the interval
      // below starts.
      void audioContext.resume().catch(() => {});

      const frequencyBins = new Uint8Array(analyser.frequencyBinCount);
      const timeDomainBytes = new Uint8Array(analyser.fftSize);
      const ranges = bandBinRanges(audioContext.sampleRate, analyser.frequencyBinCount);

      this.audioContext = audioContext;
      this.intervalHandle = setInterval(() => {
        analyser.getByteFrequencyData(frequencyBins);
        analyser.getByteTimeDomainData(timeDomainBytes);

        onSample({
          bands: sampleBands(frequencyBins, ranges),
          rms: rmsOfTimeDomainBytes(timeDomainBytes),
        });
      }, SAMPLE_INTERVAL_MS);
    } catch (error) {
      console.warn('Voice: failed to set up band analysis for a peer.', error);
    }
  }

  /** A no-op if not currently tracking. */
  stop(): void {
    if (this.intervalHandle !== null) {
      clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }

    if (this.audioContext) {
      void this.audioContext.close().catch(() => {});
      this.audioContext = null;
    }
  }
}
